import {IAutoOrderOrder, IAutoOrderOrderPreparedInfoDetails} from '../models';

/**
 * Counts the prepared lines on an order that carry a positive quantity.
 *
 * Orders can exist in Firestore with an empty or zero-quantity prepared map, and
 * those must not be saved or submitted for processing.
 */
export function countPositivePreparedItems(order: IAutoOrderOrder | undefined): number {
  const preparedItems: Record<string, IAutoOrderOrderPreparedInfoDetails> | undefined = order?.preparedInfo?.preparedItems;
  if (!preparedItems) {
    return 0;
  }

  return Object.values(preparedItems).filter(
    (item: IAutoOrderOrderPreparedInfoDetails): boolean => Number(item?.qty) > 0,
  ).length;
}

/**
 * Returns true when an order has at least one prepared line to submit.
 */
export function hasPositivePreparedItems(order: IAutoOrderOrder | undefined): boolean {
  return countPositivePreparedItems(order) > 0;
}

/**
 * Compares two To Order quantity drafts.
 *
 * Used to tell whether the quantities changed while a prepare write was in
 * flight, so newer edits are never discarded by a write that did not include
 * them.
 */
export function areQuantityDraftsEqual(
  first: Record<string, number> | null | undefined,
  second: Record<string, number> | null | undefined,
): boolean {
  const firstDraft: Record<string, number> = first ?? {};
  const secondDraft: Record<string, number> = second ?? {};
  const firstCodes: string[] = Object.keys(firstDraft);

  if (firstCodes.length !== Object.keys(secondDraft).length) {
    return false;
  }

  return firstCodes.every((code: string): boolean => Number(firstDraft[code]) === Number(secondDraft[code]));
}
